import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import Register from './components/Register';
import VerifyEmail from './components/VerifyEmail';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './components/Dashboard';
import Accounts from './components/Accounts';
import Transactions from './components/Transactions';
import ChatPopup from './components/ChatPopup';
import Profile from './components/Profile';
import Budgets from './components/Budgets';
import Analytics from './components/Analytics';
import RoutePrefetch from './components/RoutePrefetch';
import {
  prefetchDashboard,
  prefetchAccounts,
  prefetchTransactions,
  prefetchBudgets,
  prefetchAnalytics,
  prefetchProfile,
} from './services/prefetch';
import './App.css';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1a1a1a', // Mercury near-black
      light: '#404040',
      dark: '#000000',
      contrastText: '#fff',
    },
    secondary: {
      main: '#5b4d3a', // warm taupe accent
      light: '#8a7a64',
      dark: '#3a2f22',
      contrastText: '#fff',
    },
    success: {
      main: '#0f7a5a',
      light: '#2fa37b',
      dark: '#0a5942',
    },
    error: {
      main: '#c43e3e',
      light: '#d96a6a',
      dark: '#9a2a2a',
    },
    warning: {
      main: '#b8842a',
      light: '#d6a64f',
      dark: '#8c631e',
    },
    info: {
      main: '#1a1a1a',
      light: '#404040',
      dark: '#000000',
    },
    background: {
      default: '#faf8f3', // Mercury warm cream
      paper: '#ffffff',
    },
    text: {
      primary: '#0a0a0a',
      secondary: '#5c5c5c',
    },
    divider: '#eaeaea',
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica", "Arial", sans-serif',
    h3: {
      fontWeight: 600,
      letterSpacing: '-0.03em',
    },
    h4: {
      fontWeight: 600,
      letterSpacing: '-0.025em',
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.015em',
    },
    h6: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    body1: {
      letterSpacing: '-0.005em',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
      letterSpacing: '-0.005em',
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(15, 15, 15, 0.04)',
    '0 1px 2px 0 rgba(15, 15, 15, 0.05)',
    '0 2px 4px 0 rgba(15, 15, 15, 0.06)',
    '0 4px 8px -2px rgba(15, 15, 15, 0.06), 0 2px 4px -2px rgba(15, 15, 15, 0.04)',
    '0 6px 12px -3px rgba(15, 15, 15, 0.07), 0 3px 6px -3px rgba(15, 15, 15, 0.04)',
    '0 8px 16px -4px rgba(15, 15, 15, 0.08), 0 4px 8px -4px rgba(15, 15, 15, 0.04)',
    '0 12px 24px -6px rgba(15, 15, 15, 0.10)',
    '0 12px 24px -6px rgba(15, 15, 15, 0.10)',
    '0 12px 24px -6px rgba(15, 15, 15, 0.10)',
    '0 12px 24px -6px rgba(15, 15, 15, 0.10)',
    '0 12px 24px -6px rgba(15, 15, 15, 0.10)',
    '0 12px 24px -6px rgba(15, 15, 15, 0.10)',
    '0 12px 24px -6px rgba(15, 15, 15, 0.10)',
    '0 12px 24px -6px rgba(15, 15, 15, 0.10)',
    '0 12px 24px -6px rgba(15, 15, 15, 0.10)',
    '0 12px 24px -6px rgba(15, 15, 15, 0.10)',
    '0 12px 24px -6px rgba(15, 15, 15, 0.10)',
    '0 12px 24px -6px rgba(15, 15, 15, 0.10)',
    '0 12px 24px -6px rgba(15, 15, 15, 0.10)',
    '0 12px 24px -6px rgba(15, 15, 15, 0.10)',
    '0 12px 24px -6px rgba(15, 15, 15, 0.10)',
    '0 12px 24px -6px rgba(15, 15, 15, 0.10)',
    '0 12px 24px -6px rgba(15, 15, 15, 0.10)',
    '0 12px 24px -6px rgba(15, 15, 15, 0.10)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: '7px 16px',
          boxShadow: 'none',
          fontSize: '0.875rem',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          background: '#1a1a1a',
          color: '#ffffff',
          '&:hover': {
            background: '#000000',
          },
        },
        outlined: {
          borderColor: '#eaeaea',
          color: '#1a1a1a',
          '&:hover': {
            borderColor: '#1a1a1a',
            background: 'transparent',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          border: '1px solid #eaeaea',
          boxShadow: 'none',
          transition: 'border-color 0.15s ease',
          '&:hover': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: {
          border: '1px solid #eaeaea',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 6,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
            '& fieldset': {
              borderColor: '#eaeaea',
            },
            '&:hover fieldset': {
              borderColor: '#c4c4c4',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#1a1a1a',
              borderWidth: 1,
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: '#faf8f3' }}>
            <Navbar />
            <Box component="main" sx={{ flexGrow: 1, paddingTop: 4 }}>
              <ChatPopup />
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                
                {/* Protected routes */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <RoutePrefetch prefetch={prefetchDashboard}>
                      <Dashboard />
                    </RoutePrefetch>
                  </ProtectedRoute>
                } />
                <Route path="/accounts" element={
                  <ProtectedRoute>
                    <RoutePrefetch prefetch={prefetchAccounts}>
                      <Accounts />
                    </RoutePrefetch>
                  </ProtectedRoute>
                } />
                <Route path="/transactions" element={
                  <ProtectedRoute>
                    <RoutePrefetch prefetch={prefetchTransactions}>
                      <Transactions />
                    </RoutePrefetch>
                  </ProtectedRoute>
                } />
                <Route path="/budgets" element={
                  <ProtectedRoute>
                    <RoutePrefetch prefetch={prefetchBudgets}>
                      <Budgets />
                    </RoutePrefetch>
                  </ProtectedRoute>
                } />
                <Route path="/analytics" element={
                  <ProtectedRoute>
                    <RoutePrefetch prefetch={prefetchAnalytics}>
                      <Analytics />
                    </RoutePrefetch>
                  </ProtectedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <RoutePrefetch prefetch={prefetchProfile}>
                      <Profile />
                    </RoutePrefetch>
                  </ProtectedRoute>
                } />
              </Routes>
            </Box>
          </Box>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;